/**
 * Mini-Project Progress tab. Stages 3b + 3c.
 *
 * Stage 3b: KPI status circles + read-only accordion sections.
 * Stage 3c: editing UIs inside each accordion when isLead=true.
 *
 * Accordion summaries derive from live sub-entity counts. Sections
 * receive isLead + onMutated. Mutations call onMutated → triggers a
 * full sub-entity refetch (simple, slightly wasteful — acceptable
 * for v1 since the Lead-only-write surface area is small).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProject } from "../../api/miniProjects";
import { projectDurationDays } from "../../Data/MiniProjectConfig/scheduleThresholds";
import { useApiMiniProjectSubEntities } from "../../api/useApiMiniProjectSubEntities";
import { computeMiniProjectKpiStatus, computeMpKpiTrends, kpiColorHex, type KpiColor } from "./computeKpiStatus";
import { useHseMiniProjectIncidents } from "../../api/hseIncidents";
import { computeHseBundle } from "../../kpi-engine/hse";
import { MP_HSE_CONFIGS } from "../../Data/MiniProjectConfig/miniProjectHseConfigs";
import MiniProjectKpiNavigation from "../../Components/MiniProjectDetail/MiniProjectKpiNavigation";
import MiniProjectSummaryTab from "../../Components/MiniProjectDetail/MiniProjectSummaryTab";
import MiniProjectScheduleTab from "../../Components/MiniProjectDetail/MiniProjectScheduleTab";
import MiniProjectMissionTab from "../../Components/MiniProjectDetail/MiniProjectMissionTab";
import MiniProjectBudgetTab from "../../Components/MiniProjectDetail/MiniProjectBudgetTab";
import MiniProjectDataReliabilityTab from "../../Components/MiniProjectDetail/MiniProjectDataReliabilityTab";
import type { KpiSubTabKey } from "../../Components/MiniProjectDetail/MiniProjectKpiNavigation";
import GoalsSection from "./sections/GoalsSection";
import TasksSection from "./sections/TasksSection";
// Team section relocated to the About tab per Skip's 2026-05-19 decision
// (Team is "who", not "what's happening" — belongs with About narrative).
import BomSection from "./sections/BomSection";
// Progress visibility toggle relocated into the Settings modal per
// Skip's 2026-05-19 redirect. Lead opens the modal via the gear icon
// in the header.
import StatusSection from "../../Components/StatusSection/StatusSection";
import StatusWriter from "../../Components/StatusWriter/StatusWriter";
import StatusReader from "../../Components/StatusReader/StatusReader";
import ScheduleModal from "../../Components/ScheduleModal/ScheduleModal";
import MissionModal from "../../Components/MissionModal/MissionModal";
import BudgetModal from "../../Components/BudgetModal/BudgetModal";
import DataReliabilityModal from "../../Components/DataReliabilityModal/DataReliabilityModal";
import {
  fetchLatestMiniProjectStatus,
  fetchMiniProjectStatuses,
  fetchMiniProjectStatus,
  type MiniProjectStatus,
  type MiniProjectStatusSummary,
} from "../../api/miniProjectStatuses";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import styles from "./ProgressTab.module.css";

interface Props {
  miniProject: MiniProject;
  isLead: boolean;
  currentUserGuid: string;
  onMutated: () => void;
  /**
   * Task #213. When set, ProgressTab auto-opens the matching KPI modal
   * on mount (and whenever this value changes). Used by the portfolio
   * split-screen panel: clicking a KPI dot on an MP row in the grid
   * routes the user to the Progress tab AND opens the relevant modal
   * in one click. `null`/undefined leaves the tab in its default state
   * (no modal). Bumping the parent-side request id triggers re-open
   * even if the same KPI is clicked twice — important when the user
   * closed the modal and then clicked the dot again.
   */
  autoOpenKpi?: "schedule" | "mission" | "budget" | "dataReliability" | "summary" | null;
  autoOpenKpiRequestId?: number;
}

export default function ProgressTab({ miniProject, isLead, currentUserGuid, onMutated, autoOpenKpi, autoOpenKpiRequestId }: Props) {
  const { t } = useAppTranslation("pages");
  const sub = useApiMiniProjectSubEntities(miniProject.miniProjectGuid);
  void onMutated;

  // DEV-1141: HSE incidents drive the floor cap on the Mission + Summary KPI
  // nav-tab colors (parallel to the FP KpiSummaryHeader). Same cache key as
  // the Mission tab body, so this is a single shared fetch. The bundle is
  // computed after `status` below.
  const { incidents: hseIncidents } = useHseMiniProjectIncidents(
    miniProject.miniProjectGuid,
  );

  // Project duration drives the auto-scaling Schedule thresholds
  // (DEV-1080, Chunk B). Computed once per render — passed to the
  // writer and modal so both apply the same rule.
  const durationDays = useMemo(
    () => projectDurationDays(miniProject.startDate, miniProject.endDate),
    [miniProject.startDate, miniProject.endDate],
  );

  // ── Status modal state (DEV-1079) ──
  // Lead's Summary KPI click opens StatusWriter in create mode; public's
  // click opens StatusReader. Lead's edit pencil on StatusSection opens
  // StatusWriter in edit mode against the currently-viewed status.
  // All three need latestStatus + filedDates context.
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [writerMode, setWriterMode] = useState<"create" | "edit">("create");
  const [editingStatus, setEditingStatus] = useState<MiniProjectStatus | null>(null);
  const [latestStatus, setLatestStatus] = useState<MiniProjectStatus | null>(null);
  const [filedDates, setFiledDates] = useState<string[]>([]);
  const [readerStatus, setReaderStatus] = useState<MiniProjectStatus | null>(null);
  // ── KPI modal state (DEV-1080, Chunk B) ──
  // Schedule modal is time-series only — it consumes summaries (which now
  // include planned/actual after the DTO extension) rather than full status
  // payloads. Source of truth for the data is still mini_project_statuses.
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [missionModalOpen, setMissionModalOpen] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [dataRelModalOpen, setDataRelModalOpen] = useState(false);
  const [statusSummaries, setStatusSummaries] = useState<MiniProjectStatusSummary[]>([]);

  // Task #213. Honor the autoOpenKpi hint from the portfolio split-screen
  // panel: when the user clicks a KPI dot on an MP row in the grid, the
  // panel mounts ProgressTab with autoOpenKpi set to the KPI they clicked.
  // We translate that into the matching modal-open state. The request id
  // is the re-trigger handle — bump it on the parent to re-open the same
  // modal after the user closed it.
  //
  // "summary" mirrors the existing Summary-dot click behavior on the
  // Progress tab itself: Lead opens StatusWriter (smart-open: edit if
  // today is already filed, else create); non-Lead opens StatusReader
  // against the latest status. Both paths require latestStatus to have
  // resolved — if it hasn't yet, the effect re-fires when latestStatus
  // updates via the dep array.
  useEffect(() => {
    if (!autoOpenKpi) return;
    // 2026-05-27 — KPI click on the portfolio KPIGridTable used to
    // open the legacy KPI modals (setScheduleModalOpen / etc). Now
    // that the four KPI sub-tabs live inside MiniProjectKpiNavigation
    // and activeKpiSubTab is lifted to this component, route the click
    // straight to the matching sub-tab instead. The modals stay
    // implemented (still imported / state still declared) until task
    // #233 / the modal deprecation sweep, but no path opens them.
    if (autoOpenKpi === "schedule") setActiveKpiSubTab("schedule");
    else if (autoOpenKpi === "mission") setActiveKpiSubTab("mission");
    else if (autoOpenKpi === "budget") setActiveKpiSubTab("budget");
    else if (autoOpenKpi === "dataReliability") setActiveKpiSubTab("data");
    else if (autoOpenKpi === "summary") {
      // 2026-05-27 — Summary just opens the Summary sub-tab. No more
      // smart-open StatusWriter for Lead, no StatusReader for non-Lead.
      // Skip's call: every KPI click behaves uniformly across the five
      // sub-tabs (just navigate). The Status section on the Summary
      // sub-tab still carries its own edit/new affordances; the KPI
      // shape is purely navigational.
      setActiveKpiSubTab("summary");
    }
  }, [autoOpenKpi, autoOpenKpiRequestId]);

  // Section "add" state lifted from each Section component so the
  // header "+" button in SectionPanel can trigger it (DEV-1081 Chunk D).
  const [addingTask, setAddingTask] = useState(false);
  const [addingBomItem, setAddingBomItem] = useState(false);
  const [addingGoal, setAddingGoal] = useState(false);

  // Active KPI sub-tab — lifted from MiniProjectKpiNavigation on
  // 2026-05-27 so the tab survives remounts triggered by sub.refetch.
  // Symptom that drove the fix: editing a task on the Schedule sub-tab
  // and saving snapped the user back to Summary because the post-save
  // refetch was redrawing the tree and resetting local state in the
  // nav component. Owning the state here makes it persistent for the
  // lifetime of ProgressTab.
  const [activeKpiSubTab, setActiveKpiSubTab] = useState<KpiSubTabKey>("summary");

  // Previous full status (the one filed before the latest). Used by
  // the KPI trend computation to compare the Mission aggregate now vs
  // previously — the summaries don't carry goalAssessments, so we
  // fetch the full payload for the 2nd-newest filed status when
  // there are at least 2. One extra fetch, only when ≥2 statuses
  // exist. Skip 2026-05-27.
  const [previousFullStatus, setPreviousFullStatus] = useState<MiniProjectStatus | null>(null);

  const loadStatusContext = useCallback(async () => {
    try {
      const [summaries, latest] = await Promise.all([
        fetchMiniProjectStatuses(miniProject.miniProjectGuid, { limit: 200 }),
        fetchLatestMiniProjectStatus(miniProject.miniProjectGuid),
      ]);
      setStatusSummaries(summaries);
      setFiledDates(summaries.map((s) => s.reportingDate));
      setLatestStatus(latest);
      if (!readerStatus && latest) setReaderStatus(latest);
      // Trend prep — fetch the previous full status for Mission trend
      // computation. summaries[0] is newest; summaries[1] is the one
      // we need. When fewer than 2 statuses exist, clear so the trend
      // computation reads null and shows circles.
      if (summaries.length >= 2) {
        const prev = await fetchMiniProjectStatus(
          miniProject.miniProjectGuid,
          summaries[1].statusGuid,
        );
        setPreviousFullStatus(prev);
      } else {
        setPreviousFullStatus(null);
      }
    } catch {
      // Soft-fail; the on-page StatusSection has its own retry path.
    }
  }, [miniProject.miniProjectGuid, readerStatus]);

  useEffect(() => {
    void loadStatusContext();
  }, [loadStatusContext]);

  // Refresh on persist event (e.g., StatusWriter just saved a new status).
  // A status save writes to TWO tables: mini_project_statuses (drives
  // latestStatus → Schedule dot) AND mission_metric_points (drives goal
  // latestAssessment → Mission dot, post-DEV-1116). Refresh both, or the
  // Mission dot stays stale until something else triggers sub.refetch.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { miniProjectGuid?: string } | undefined;
      if (!detail?.miniProjectGuid) return;
      if (detail.miniProjectGuid.toUpperCase() !== miniProject.miniProjectGuid.toUpperCase()) return;
      void loadStatusContext();
      sub.refetch();
    };
    window.addEventListener("l4e_miniproject_status_persisted", handler);
    return () => window.removeEventListener("l4e_miniproject_status_persisted", handler);
  }, [miniProject.miniProjectGuid, loadStatusContext, sub.refetch]);

  // Reader date navigation: viewer picks an older date → fetch that status.
  const handleReaderDateChange = useCallback(
    async (iso: string) => {
      try {
        const summaries = await fetchMiniProjectStatuses(miniProject.miniProjectGuid, { limit: 200 });
        const match = summaries.find((s) => s.reportingDate === iso);
        if (!match) return;
        const full = await fetchMiniProjectStatus(miniProject.miniProjectGuid, match.statusGuid);
        setReaderStatus(full);
      } catch {
        /* surface via reader's own retry path if needed */
      }
    },
    [miniProject.miniProjectGuid],
  );

  // Resolve poster name for the reader's byline.
  const { securityUsers } = useApiSecurityData();
  const posterMeta = useCallback((status: MiniProjectStatus | null) => {
    if (!status) return { posterName: undefined, posterInitials: undefined };
    const guid = status.postedByUserGuid.toUpperCase();
    const rows = (securityUsers || []) as any[];
    const user = rows.find((u) => String(u.user_guid).toUpperCase() === guid);
    const name: string | undefined =
      user?.display_name || user?.name || user?.screen_name || undefined;
    const initials = name
      ? name.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("")
      : "?";
    return { posterName: name, posterInitials: initials };
  }, [securityUsers]);

  const handleScheduleKpiClick = useCallback(() => {
    setScheduleModalOpen(true);
  }, []);

  const handleMissionKpiClick = useCallback(() => {
    setMissionModalOpen(true);
  }, []);

  const handleBudgetKpiClick = useCallback(() => {
    setBudgetModalOpen(true);
  }, []);

  const handleDataRelKpiClick = useCallback(() => {
    setDataRelModalOpen(true);
  }, []);

  const handleSummaryKpiClick = useCallback(() => {
    // Smart-open: if today already has a status, open it in edit mode
    // (so the Lead can amend it without having to find the pencil
    // affordance on StatusSection). Otherwise open a fresh create form.
    // This is the natural mental model — clicking the Summary KPI means
    // "I want to do something about today's status," not strictly "post
    // a new one."
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    if (latestStatus && latestStatus.reportingDate === todayIso) {
      setWriterMode("edit");
      setEditingStatus(latestStatus);
    } else {
      setWriterMode("create");
      setEditingStatus(null);
    }
    setStatusModalOpen(true);
  }, [latestStatus]);

  const handleEditStatus = useCallback((status: MiniProjectStatus) => {
    setWriterMode("edit");
    setEditingStatus(status);
    setStatusModalOpen(true);
  }, []);

  /** Force create mode (not the Summary KPI's smart route). Used by the
   *  "+ New" button on StatusSection so the Lead can always post a fresh
   *  status — back-dated or for a future day not yet filed — with the
   *  prefill banner from the latest status if one exists. */
  const handleNewStatus = useCallback(() => {
    setWriterMode("create");
    setEditingStatus(null);
    setStatusModalOpen(true);
  }, []);

  const handleStatusModalClose = useCallback(() => {
    setStatusModalOpen(false);
    // Clear edit state so a subsequent "post new" click isn't poisoned
    // by a stale editingStatus reference.
    setWriterMode("create");
    setEditingStatus(null);
  }, []);

  const handleStatusSaved = useCallback(() => {
    // StatusWriter already fires l4e_miniproject_status_persisted, which
    // triggers loadStatusContext via the listener above. Nothing extra
    // to do here.
  }, []);

  if (sub.loading) return <div className={styles.statusMessage}>{t("miniProject.progress.loading")}</div>;
  if (sub.error) return <div className={styles.statusMessage}>{t("miniProject.progress.failed", { error: sub.error })}</div>;

  const status = computeMiniProjectKpiStatus(
    miniProject,
    sub.goals,
    sub.tasks,
    sub.bomItems,
    latestStatus,
  );

  // DEV-1141: HSE floor cap. An open incident caps the Mission + Summary
  // nav-tab colors (worse-of) and lights the adjustment dot on the capped
  // tabs — the MP parallel of the FP KpiSummaryHeader floor cap.
  const hseBundle = computeHseBundle(
    MP_HSE_CONFIGS,
    hseIncidents,
    new Date().toISOString().slice(0, 10),
  );

  // KPI trend (2026-05-27) — compare latest vs previous filed status,
  // color-vs-color. Schedule and Budget use summaries; Mission needs
  // the full statuses for goalAssessments. DR is always null (no
  // snapshot infrastructure). First status case → all trends null →
  // all circles. See computeMpKpiTrends for the locked rules.
  const kpiTrend = computeMpKpiTrends({
    latestSummary: statusSummaries[0] ?? null,
    previousSummary: statusSummaries[1] ?? null,
    latestFull: latestStatus,
    previousFull: previousFullStatus,
    durationDays,
  });
  const readerMeta = posterMeta(readerStatus);
  void currentUserGuid;

  const goalsOnPace = sub.goals.filter((g) =>
    g.targetValue && g.targetValue > 0 && (g.currentValue ?? 0) >= g.targetValue * 0.7
  ).length;
  const tasksDone = sub.tasks.filter((t) => t.status === "done").length;
  const bomObtained = sub.bomItems.filter((b) => b.procurementState === "obtained").length;
  const bomNeeded = sub.bomItems.filter((b) => b.procurementState === "needed").length;

  return (
    <div className={styles.progressTab}>
      {/* Tasks #225 / #228 / #229 / #230 / #231 / #232 — all five
          Management sub-tabs are now in dedicated components:
            Summary  → MiniProjectSummaryTab
            Schedule → MiniProjectScheduleTab
            Budget   → MiniProjectBudgetTab          (#232, 2026-05-28)
            Mission  → MiniProjectMissionTab
            Data Rel → MiniProjectDataReliabilityTab (#231, 2026-05-28)
          With Budget and Data Reliability landed, the hidden magazine
          spread below (Tasks / Resources / Goals) is now fully
          duplicated by the sub-tab content and is safe to delete in
          a follow-up cleanup pass. The four legacy KPI modals
          (Schedule/Mission/Budget/DataReliability) still mount below
          for the portfolio split-screen autoOpenKpi path — modal
          deprecation is a separate follow-up. */}
      <MiniProjectKpiNavigation
        kpiStatus={status}
        kpiTrend={kpiTrend}
        hseWorst={hseBundle.worst_color}
        hseCappedByCategory={hseBundle.capped_by_category}
        activeSubTab={activeKpiSubTab}
        onActiveSubTabChange={setActiveKpiSubTab}
        summaryContent={
          <MiniProjectSummaryTab
            miniProject={miniProject}
            goals={sub.goals}
            isLead={isLead}
            scheduleColor={status.schedule}
            onEditStatus={isLead ? handleEditStatus : undefined}
            onNewStatus={isLead ? handleNewStatus : undefined}
          />
        }
        scheduleContent={
          // Task #229. Schedule sub-tab body: Work Completed bar →
          // planned/actual chart → Tasks section. Owns its own
          // composition; reads from the same statusSummaries +
          // sub.tasks already in scope here, and shares the
          // addingTask state with the (now-deletable) hidden magazine
          // Tasks panel.
          <MiniProjectScheduleTab
            miniProject={miniProject}
            statusSummaries={statusSummaries}
            scheduleColor={status.schedule}
            tasks={sub.tasks}
            isLead={isLead}
            onTasksMutated={sub.refetch}
            addingTask={addingTask}
            onOpenAddTask={() => setAddingTask(true)}
            onCloseAddTask={() => setAddingTask(false)}
          />
        }
        budgetContent={
          // Task #232. Budget sub-tab body: stats (needed/obtained
          // counts) → per-currency cost rollup (only if any item has
          // cost data) → Resources section (Lead-editable BomSection)
          // → grey-note disclaimer. Honest pattern: no placeholder
          // columns for actual-cost data the schema doesn't yet
          // support. The actual-cost work is filed as a separate
          // Jira ticket. Shares addingBomItem state with the hidden
          // magazine Resources panel.
          <MiniProjectBudgetTab
            miniProject={miniProject}
            bomItems={sub.bomItems}
            isLead={isLead}
            onBomMutated={sub.refetch}
            addingBomItem={addingBomItem}
            onOpenAddBomItem={() => setAddingBomItem(true)}
            onCloseAddBomItem={() => setAddingBomItem(false)}
          />
        }
        missionContent={
          // Task #230. Mission sub-tab body: aggregate one-liner →
          // HSE placeholder row → Goals section → goals×statuses
          // heatmap. HSE is a static placeholder; the MP-side HSE
          // backend pipeline (auto-provisioning + polymorphic
          // project_issues + computeHseBundle adapter) is filed as a
          // separate Jira ticket. Heatmap fans out one fetch per
          // filed status to get the per-goal assessment arrays.
          <MiniProjectMissionTab
            miniProject={miniProject}
            statusSummaries={statusSummaries}
            goals={sub.goals}
            isLead={isLead}
            onGoalsMutated={sub.refetch}
            addingGoal={addingGoal}
            onOpenAddGoal={() => setAddingGoal(true)}
            onCloseAddGoal={() => setAddingGoal(false)}
          />
        }
        dataContent={
          // Task #231. Data Reliability sub-tab body: headline card
          // (band chip + big number "X days" + threshold bar) →
          // last-update detail → Lead or public hint. Read-only —
          // the value is auto-derived from the most recent
          // sub-entity write, no edit surface here. Thresholds
          // mirror computeKpiStatus.ts (canonical source).
          <MiniProjectDataReliabilityTab
            miniProject={miniProject}
            isLead={isLead}
          />
        }
      />

      {/* Magazine spread sections kept inline below for now — they'll
          relocate to their respective sub-tabs (#229/#230/#232). The
          surrounding code block stays so the SectionPanel imports and
          handlers don't go stale before the relocation lands.
          Wrapping in a hidden div for the transition window. */}
      <div style={{ display: "none" }}>
      <div className={styles.pairings}>
        <SectionPanel
          title={t("miniProject.progress.section.tasks")}
          icon="☑"
          summary={t("miniProject.progress.section.tasksSummary", { done: tasksDone, total: sub.tasks.length })}
          onAdd={isLead ? () => setAddingTask(true) : undefined}
          addLabel={t("miniProject.progress.section.addTask")}
        >
          <TasksSection
            miniProjectGuid={miniProject.miniProjectGuid}
            tasks={sub.tasks}
            isLead={isLead}
            onMutated={sub.refetch}
            addOpen={addingTask}
            onCloseAdd={() => setAddingTask(false)}
          />
        </SectionPanel>

        <SectionPanel
          title={t("miniProject.progress.section.resources")}
          icon="📦"
          summary={sub.bomItems.length === 0
            ? t("miniProject.progress.section.resourcesEmpty")
            : bomNeeded > 0
              ? t("miniProject.progress.section.resourcesSummaryWithNeeded", {
                  obtained: bomObtained,
                  total: sub.bomItems.length,
                  needed: bomNeeded,
                })
              : t("miniProject.progress.section.resourcesSummary", {
                  obtained: bomObtained,
                  total: sub.bomItems.length,
                })}
          onAdd={isLead ? () => setAddingBomItem(true) : undefined}
          addLabel={t("miniProject.progress.section.addResource")}
        >
          <BomSection
            miniProjectGuid={miniProject.miniProjectGuid}
            bomItems={sub.bomItems}
            isLead={isLead}
            onMutated={sub.refetch}
            addOpen={addingBomItem}
            onCloseAdd={() => setAddingBomItem(false)}
          />
        </SectionPanel>

        <SectionPanel
          title={t("miniProject.progress.section.goals")}
          icon="🎯"
          summary={
            (sub.goals.length === 1
              ? t("miniProject.progress.section.goalsSummaryOne")
              : t("miniProject.progress.section.goalsSummaryPlural", { count: sub.goals.length })) +
            (sub.goals.length > 0
              ? t("miniProject.progress.section.goalsOnPaceSuffix", { onPace: goalsOnPace })
              : "")
          }
          onAdd={isLead ? () => setAddingGoal(true) : undefined}
          addLabel={t("miniProject.progress.section.addGoal")}
        >
          <GoalsSection
            miniProjectGuid={miniProject.miniProjectGuid}
            goals={sub.goals}
            isLead={isLead}
            onMutated={sub.refetch}
            addOpen={addingGoal}
            onCloseAdd={() => setAddingGoal(false)}
          />
        </SectionPanel>
      </div>
      </div>

      {/* DEV-1079 — Status modals. Lead's Summary KPI click opens the
          writer; public's click opens the reader. Both modals are
          conditionally rendered to avoid mounting TipTap / fetching
          status until needed. */}
      {statusModalOpen && isLead && (
        <StatusWriter
          open={statusModalOpen}
          onClose={handleStatusModalClose}
          miniProjectGuid={miniProject.miniProjectGuid}
          mode={writerMode}
          editingStatus={editingStatus}
          // DEV-822 — HSE incident trackers are never a cadenced assessment;
          // exclude them from the status writer's goal-assessment rows
          // (mirrors the backend activeGoals exclusion).
          goals={sub.goals.filter((g) => g.missionTypeKey !== "incident_count")}
          // In edit mode, the previousStatus pre-fill banner is suppressed
          // by StatusWriter regardless of this prop, but pass null to be
          // explicit.
          previousStatus={writerMode === "edit" ? null : latestStatus}
          filedDates={filedDates}
          durationDays={durationDays}
          // Migration 190 — Budget Progress section needs the planned
          // budget and current recorded spend to compute its auto-suggest
          // chip. recordedSpend sums server-computed actualTotal across
          // all BOM items.
          plannedBudgetAmount={miniProject.plannedBudgetAmount ?? null}
          plannedBudgetCurrency={miniProject.plannedBudgetCurrency ?? null}
          recordedSpend={sub.bomItems.reduce(
            (sum, b) => sum + (b.actualTotal ?? 0),
            0,
          )}
          onSaved={handleStatusSaved}
          // 2026-05-27 — Lead can update goal actuals from inside the
          // status modal via the new in-modal data-entry tray. Wire
          // sub.refetch so the refreshed currentValue flows back into
          // the goals[] prop and the inline meta row re-renders. We
          // intentionally do NOT close the status modal here — that's
          // what onSaved is for.
          onGoalDataChanged={sub.refetch}
        />
      )}
      {statusModalOpen && !isLead && (
        <StatusReader
          open={statusModalOpen}
          onClose={handleStatusModalClose}
          status={readerStatus}
          filedDates={filedDates}
          onDateChange={handleReaderDateChange}
          posterName={readerMeta.posterName}
          posterInitials={readerMeta.posterInitials}
        />
      )}

      {/* Schedule KPI modal (DEV-1080, Chunk B). Time-series view of
          planned/actual % from filed statuses. Open to all viewers;
          source-of-truth edits happen through the Status writer. */}
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          summaries={statusSummaries}
          durationDays={durationDays}
          isLead={isLead}
        />
      )}

      {/* Mission KPI modal (DEV-1080, Chunk B). Per-goal assessment
          history across filed statuses. Fans out full status fetches
          on open; resets between opens to stay fresh after edits. */}
      {missionModalOpen && (
        <MissionModal
          open={missionModalOpen}
          onClose={() => setMissionModalOpen(false)}
          miniProjectGuid={miniProject.miniProjectGuid}
          goals={sub.goals}
          summaries={statusSummaries}
          isLead={isLead}
        />
      )}

      {/* Budget KPI modal (DEV-1080, Chunk B). BOM summary as the
          best-available budget view in v1 — KPI shape stays grey because
          budget thresholds aren't defined yet. */}
      {budgetModalOpen && (
        <BudgetModal
          open={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          bomItems={sub.bomItems}
          isLead={isLead}
        />
      )}

      {/* Data Reliability KPI modal (DEV-1080, Chunk B). Staleness
          diagnostic showing days since last update, where this project
          sits on the threshold band, and how to refresh the signal. */}
      {dataRelModalOpen && (
        <DataReliabilityModal
          open={dataRelModalOpen}
          onClose={() => setDataRelModalOpen(false)}
          miniProject={miniProject}
          isLead={isLead}
        />
      )}
    </div>
  );
}

function KpiCard({ label, color, onClick }: { label: string; color: KpiColor; onClick?: () => void }) {
  const { t } = useAppTranslation("pages");
  const clickable = !!onClick;
  return (
    <div
      className={styles.kpiCard}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }
        : undefined}
      style={clickable ? { cursor: "pointer" } : undefined}
    >
      <div className={styles.kpiLabel}>{label}</div>
      <div
        className={styles.kpiCircle}
        style={{ background: kpiColorHex(color) }}
        aria-label={t("miniProject.progress.kpi.statusAria", { label, color })}
      />
    </div>
  );
}

/** SectionPanel — always-open panel with a fixed-height scrolling body.
 *  Replaces the old Accordion (DEV-1081 Chunk C). The body scrolls
 *  internally so a long list never blows out the 3-column magazine
 *  layout below the Status hero.
 *
 *  Add affordance lives in the header (DEV-1081 Chunk D, Skip's
 *  2026-05-22 decision after the placement mockup session). When
 *  `onAdd` is provided, a "+" icon button shows after the summary
 *  text. Click triggers the parent's add form. Always visible regardless
 *  of body scroll position. */
function SectionPanel({
  title,
  summary,
  icon,
  onAdd,
  addLabel,
  children,
}: {
  title: string;
  summary: string;
  icon: string;
  /** Lead-only callback. When provided, renders the "+" icon in the
   *  header. Click → parent flips its addOpen state. */
  onAdd?: () => void;
  /** aria-label for the add button (e.g. "Add task"). */
  addLabel?: string;
  children: React.ReactNode;
}) {
  const { t } = useAppTranslation("pages");
  const addBtnLabel = addLabel || t("miniProject.progress.section.addDefault", { section: title.toLowerCase() });
  return (
    <section className={styles.sectionPanel}>
      <header className={styles.sectionHeader}>
        <span className={styles.sectionIcon} aria-hidden="true">{icon}</span>
        <span className={styles.sectionTitle}>{title}</span>
        <span className={styles.sectionSummary}>{summary}</span>
        {onAdd && (
          <button
            type="button"
            className={styles.sectionAddBtn}
            onClick={onAdd}
            aria-label={addBtnLabel}
            title={addBtnLabel}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        )}
      </header>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}
